import { FormControl, Grid, TextField, Tooltip, Typography } from "@mui/material";
import { NumericFormat } from 'react-number-format';

import React, { memo, useCallback, useEffect, useRef, useState } from "react";

import { INumberFormControlProperty } from "@definitions/index";
import { FieldError } from "@components/FieldError";
import { flexCenterProps, horizontalFormControlGridLeftStyle, paddingX1Props, requiredLabelStyle } from "@styles/global-css-class";
import { StyledFormLabel } from "..";
import DiffInheritanceIndicator from "@components/DiffInheritanceIndicator";

function NewNumberFormControl({
	childKey,
	label,
	value,
	name,
	errorName,
	defaultValue,
	placeHolder,
	isRequired,
	isDisabled,
	isHidden = false,
	onChange,
	formState,
	decimalDigits,
	xsFormControl = 12,
	mdFormControl = 12,
	xsLabel = 0,
	xsInput,
	textAlign,
	textErrorAlign,
	itemSx,
	textSx,
	isHideLabel = true,
	labelToolTip,
	min,
	max,
	endAdornmentLabel,
	decimalSeparator = ".",
	thousandSeparator = true,
	allowLeadingZeros,
	allowNegative = false,
	fixedDecimalScale,
	isExpanded,
	isPercentNumber,
	showIndicator,
	description,
	isIndicatorTakeSpace,
	...props
}: Readonly<INumberFormControlProperty>) {
	const decimalPlaceholder = decimalDigits && decimalDigits > 0 ? "0." + "0".repeat(decimalDigits) : "0";
	const inputRef = useRef<HTMLInputElement>(null);
	const [valueInput, setValueInput] = useState(value ?? defaultValue ?? '');

	const validPercentage = (value: number) => {
		const minValue = min ?? 0;
		const maxValue = max ?? 100;
		let result = value > maxValue ? maxValue : value;
		result = result < minValue ? minValue : result;
		return result;
	};

	useEffect(() => {
		const floatValue = parseFloat(value);
		if (floatValue !== valueInput) {
			if (endAdornmentLabel?.trim().startsWith("%") || isPercentNumber) {
				setValueInput(isNaN(floatValue) ? "" : validPercentage(floatValue));
			}
			else {
				setValueInput(isNaN(floatValue) ? "" : floatValue);
			}

		}
	}, [value]);

	useEffect(() => {
		if (isExpanded && inputRef.current) {
			inputRef.current?.focus();
		}
	}, [isExpanded, inputRef.current])

	return <Grid container item xs={xsFormControl} md={mdFormControl} sx={itemSx ?? {
		...paddingX1Props,
		alignContent: 'flex-start'
	}}>
		<FormControl key={`${childKey}-form-control`} fullWidth margin='normal' {...props}>
			<Grid container item sx={flexCenterProps}>
				{!isHideLabel && <Grid item xs={xsLabel} sx={itemSx ?? (description === undefined ? horizontalFormControlGridLeftStyle : {
					display: "flex", flexDirection: "column"
				})}>
					<StyledFormLabel
						aria-describedby={name}
						hidden={isHidden}
						error={formState?.errors[errorName ?? name] !== undefined}
						sx={textSx ?? {
							...requiredLabelStyle,
							textAlign: textAlign ?? "right"
						}}>
						{label}
					</StyledFormLabel>
					{description && <Typography sx={{ ...requiredLabelStyle, fontSize: "small", color: "#181d1f", fontWeight: "300" }}>{description}</Typography>}
				</Grid>}
				<Grid container item xs={xsInput ?? (xsLabel === 12 ? 12 : 12 - xsLabel)} sx={{ position: "relative", width: '100%' }} flexWrap="nowrap">
					{labelToolTip ? <Tooltip title={labelToolTip}>
						<NumericFormat
							key={`${childKey}-form-control-input`}
							id={name}
							name={name}
							placeholder={placeHolder ?? decimalPlaceholder}
							disabled={isDisabled ?? false}
							hidden={isHidden}
							required={isRequired}
							value={valueInput}
							onFocus={(e) => {
								const parseValue = parseFloat(e.target.value)
								if (parseValue === 0) {
									setValueInput("")
								}
							}}
							onChange={(e) => {
								setValueInput(e.target.value)
							}}
							onBlur={(e) => {
								const floatValue = parseFloat(e.target.value?.replaceAll(",", ""));
								let result: number | null = null;
								if (endAdornmentLabel?.trim().startsWith("%")) {
									result = isNaN(floatValue) ? null : validPercentage(floatValue);
									setValueInput(result);
									onChange(result);
								}
								else {
									result = isNaN(floatValue) ? null : floatValue;
									setValueInput(result);
									onChange(result);
								}
							}}
							customInput={TextField}
							thousandSeparator={thousandSeparator}
							allowLeadingZeros={allowLeadingZeros}
							allowNegative={allowNegative}
							readOnly={isDisabled}
							min={min}
							max={max}
							error={formState?.errors[errorName ?? name] !== undefined}
							decimalSeparator={decimalSeparator}
							thousandsGroupStyle="thousand"
							decimalScale={decimalDigits}
							fixedDecimalScale={fixedDecimalScale}
							valueIsNumericString
							label={label}
							suffix={endAdornmentLabel}
							sx={{
								'& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
									display: 'none'
								},
								width: "100%",
							}}
							inputProps={{ ref: inputRef }}
						/>
					</Tooltip> :
						<Grid item xs={isIndicatorTakeSpace ? 11 : 12} sx={{ position: "relative" }}>
							<NumericFormat
								key={`${childKey}-form-control-input`}
								id={name}
								name={name}
								placeholder={placeHolder ?? decimalPlaceholder}
								disabled={isDisabled ?? false}
								hidden={isHidden}
								required={isRequired}
								value={valueInput}
								onFocus={(e) => {
									const parseValue = parseFloat(e.target.value)
									if (parseValue === 0) {
										setValueInput("")
									}
								}}
								onChange={(e) => {
									setValueInput(e.target.value)
								}}
								onBlur={(e) => {
									const floatValue = parseFloat(e.target.value?.replaceAll(",", ""));
									let result: number | null = null;
									if (endAdornmentLabel?.trim().startsWith("%") || isPercentNumber) {
										result = isNaN(floatValue) ? null : validPercentage(floatValue);
										setValueInput(result);
										onChange(result);
									}
									else {
										result = isNaN(floatValue) ? null : floatValue;
										setValueInput(result);
										onChange(result);
									}
								}}
								customInput={TextField}
								thousandSeparator={thousandSeparator}
								allowLeadingZeros={allowLeadingZeros}
								allowNegative={allowNegative}
								readOnly={isDisabled}
								min={min}
								max={max}
								error={formState?.errors[errorName ?? name] !== undefined}
								decimalSeparator={decimalSeparator}
								thousandsGroupStyle="thousand"
								decimalScale={decimalDigits}
								fixedDecimalScale={fixedDecimalScale}
								valueIsNumericString
								label={label}
								suffix={endAdornmentLabel}
								sx={{
									'& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
										display: 'none'
									},
									width: "100%",
								}}
								inputProps={{ ref: inputRef }}
							/>
							{formState && <FieldError key={`${childKey}-form-control-error`} formState={formState} name={errorName ?? name} textAlign={textErrorAlign} />}
						</Grid>
					}
					{showIndicator !== undefined && showIndicator() === true && (
						<Grid xs={isIndicatorTakeSpace ? 1 : 0} item sx={{ ...flexCenterProps }}>
							<DiffInheritanceIndicator />
						</Grid>
					)}
				</Grid>
			</Grid>
		</FormControl>
	</Grid>;
}

export default memo(NewNumberFormControl);
